import React, { useEffect, useState } from "react";
import { Workspace } from "polotno/canvas/workspace";
import { storeToVideo } from '@polotno/video-export';
import Konva from "konva";
import { getApiBaseUrl } from "../api/utils";

const API_URL = getApiBaseUrl();
const R2_PUBLIC_URL = (process.env.REACT_APP_R2_PUBLIC_URL || "").replace(/\/+$/, "");

function waitForWorkspaceFrame() {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.setTimeout(resolve, 120);
      });
    });
  });
}

async function warmMountedPages(store) {
  const pageIds = (store?.pages || []).map((page) => page.id).filter(Boolean);
  store.pages?.forEach((page) => page.set?.({ _forceMount: true, _rendering: true }));
  await waitForWorkspaceFrame();
  await waitForWorkspaceFrame();

  for (const pageId of pageIds) {
    store.selectPage(pageId);
    await store.waitLoading?.();
    await waitForWorkspaceFrame();
  }
  if (pageIds[0]) {
    store.selectPage(pageIds[0]);
    await store.waitLoading?.();
    await waitForWorkspaceFrame();
  }
}

function getMountedStagePageIds() {
  return Konva.stages.map((stage) => stage.getAttr("pageId")).filter(Boolean);
}

function shouldProxyAudioUrl(src) {
  if (!src || typeof src !== "string" || src.startsWith("data:") || src.startsWith("blob:")) {
    return false;
  }

  if (R2_PUBLIC_URL && src.startsWith(`${R2_PUBLIC_URL}/`)) {
    return true;
  }

  try {
    const parsedUrl = new URL(src);
    return parsedUrl.hostname.endsWith(".r2.dev");
  } catch (_error) {
    return false;
  }
}

function getAudioExportUrl(src) {
  if (!shouldProxyAudioUrl(src)) {
    return src;
  }
  return `${API_URL}/media/r2-proxy?url=${encodeURIComponent(src)}`;
}

async function prepareAudioForExport(store) {
  const originals = [];
  const diagnostics = [];

  (store?.audios || []).forEach((audio) => {
    const exportSrc = getAudioExportUrl(audio.src);
    if (exportSrc !== audio.src && audio.set) {
      originals.push({ audio, src: audio.src });
      audio.set({ src: exportSrc });
    }
  });

  await Promise.all(
    (store?.audios || [])
      .filter((audio) => audio.src)
      .map(async (audio) => {
        const report = {
          id: audio.id,
          name: audio.name || "Audio",
          originalSrcPrefix:
            originals.find((item) => item.audio === audio)?.src?.slice(0, 90) ||
            (typeof audio.src === "string" ? audio.src.slice(0, 90) : ""),
          exportSrcPrefix: typeof audio.src === "string" ? audio.src.slice(0, 110) : "",
          proxied: shouldProxyAudioUrl(
            originals.find((item) => item.audio === audio)?.src || audio.src
          ),
          status: "pending",
          contentType: "",
          bytes: 0,
          decodeStatus: "not_checked",
          error: "",
        };

        try {
          const response = await fetch(audio.src);
          if (!response.ok) {
            throw new Error(`${response.status} ${response.statusText}`);
          }
          const arrayBuffer = await response.arrayBuffer();
          report.contentType = response.headers.get("content-type") || "";
          report.bytes = arrayBuffer.byteLength;

          try {
            const AudioContextClass = window.AudioContext || window.webkitAudioContext;
            if (AudioContextClass) {
              const audioContext = new AudioContextClass();
              await audioContext.decodeAudioData(arrayBuffer.slice(0));
              await audioContext.close?.();
              report.decodeStatus = "ok";
            } else {
              report.decodeStatus = "audio_context_unavailable";
            }
          } catch (decodeError) {
            report.decodeStatus = "failed";
            report.error = decodeError?.message || String(decodeError);
          }

          report.status = "fetch_ok";
          console.info("[VideoViewer] Audio fetch preflight ok", {
            name: audio.name,
            contentType: report.contentType,
            bytes: report.bytes,
            decodeStatus: report.decodeStatus,
            srcPrefix: audio.src.slice(0, 110),
          });
        } catch (error) {
          report.status = "fetch_failed";
          report.error = error?.message || String(error);
          console.warn("[VideoViewer] Audio fetch preflight failed", {
            name: audio.name,
            srcPrefix: typeof audio.src === "string" ? audio.src.slice(0, 110) : "",
            error,
          });
        } finally {
          diagnostics.push(report);
        }
      })
  );

  return {
    diagnostics,
    restore: () => {
      originals.forEach(({ audio, src }) => audio.set?.({ src }));
    },
  };
}

async function exportStoreToVideoWhenWorkspaceIsReady({ store, fps, pixelRatio, onProgress, onDiagnostics }) {
  let restoreAudioUrls = () => {};
  try {
    await warmMountedPages(store);
    const audioPreparation = await prepareAudioForExport(store);
    restoreAudioUrls = audioPreparation.restore;
    onDiagnostics?.((current) => ({
      ...current,
      audioReports: audioPreparation.diagnostics,
    }));
    console.info("[VideoViewer] Starting Polotno video export", {
      pageIds: store.pages?.map((page) => page.id),
      mountedStagePageIds: getMountedStagePageIds(),
      width: store.width,
      height: store.height,
      duration: store.duration,
      audioCount: store.audios?.length || 0,
      audios: (store.audios || []).map((audio) => ({
        id: audio.id,
        name: audio.name,
        hasSrc: Boolean(audio.src),
        srcPrefix: typeof audio.src === "string" ? audio.src.slice(0, 90) : "",
        delay: audio.delay,
        startTime: audio.startTime,
        endTime: audio.endTime,
        duration: audio.duration,
        volume: audio.volume,
      })),
    });
    const blob = await storeToVideo({ store, fps, pixelRatio, onProgress, includeAudio: true });
    onDiagnostics?.((current) => ({
      ...current,
      videoBlobType: blob.type,
      videoBlobBytes: blob.size,
    }));
    return blob;
  } finally {
    restoreAudioUrls();
    store.pages?.forEach((page) => page.set?.({ _forceMount: false, _rendering: false }));
  }
}

export function VideoViewer({ store, templateId, eventId, mode }) {
  const [loading, setLoading] = useState(true);
  const [videoUrl, setVideoUrl] = useState(null);
  const [diagnostics, setDiagnostics] = useState({
    audioCount: 0,
    audioReports: [],
    videoBlobType: "",
    videoBlobBytes: 0,
    uploadedUrl: "",
    error: "",
  });

  useEffect(() => {
    if (!store) return;

    setLoading(true);
    setDiagnostics({
      audioCount: store.audios?.length || 0,
      audioReports: [],
      videoBlobType: "",
      videoBlobBytes: 0,
      uploadedUrl: "",
      error: "",
    });

    const timeout = setTimeout(async () => {
      try {
        // 🌀 Step 1: Export video from Polotno
        // Adjust FPS, duration, etc. as needed
        // const dataUrl = await store.toVideoDataURL({
        //   fps: 30,          // smooth animation
        //   quality: 1,       // highest quality
        //   duration: 5000,   // 5 seconds total
        //   format: "mp4",    // explicitly set to mp4 if supported
        // });

        // // 🧱 Step 2: Convert DataURL → Blob
        // const res = await fetch(dataUrl);
        // const blob = await res.blob();

        const videoBlob = await exportStoreToVideoWhenWorkspaceIsReady({
          store,
          fps: 30, // Frames per second (default: 30)
          pixelRatio: 2, // Pixel ratio for quality (default: 1)
          onDiagnostics: setDiagnostics,
          onProgress: (progress, frameTime) => {
            console.log(`Progress: ${Math.round(progress * 100)}%`);
            console.log(`Frame render time: ${frameTime}ms`);
          },
        });

        // 📦 Step 3: Build FormData for upload
        const formData = new FormData();
        formData.append("file", videoBlob, "animation.mp4");
        formData.append("id", mode === "template" ? templateId : eventId);
        formData.append("updateType", mode);
        formData.append("fileType", "video");

        const uploadUrl = `${API_URL}/media/polotno_upload_gif_video`;

        console.log("Uploading to:", uploadUrl);

        // 🚀 Step 4: Upload to FastAPI
        const res1 = await fetch(uploadUrl, { method: "POST", body: formData });
        if (!res1.ok) throw new Error(`Upload failed: ${res1.statusText}`);
        const data = await res1.json();

        // 🎥 Step 5: Display uploaded video
        const uploadedVideoUrl = data.file_url || data.video_url || data.media_url;
        setVideoUrl(uploadedVideoUrl);
        setDiagnostics((current) => ({
          ...current,
          uploadedUrl: uploadedVideoUrl || "",
        }));
      } catch (err) {
        console.error("Video upload error:", err);
        setDiagnostics((current) => ({
          ...current,
          error: err?.message || String(err),
        }));
      } finally {
        setLoading(false);
      }
    }, 2000);

    return () => clearTimeout(timeout);
  }, [eventId, mode, store, templateId]);

  if (!store) return <div>No store found (try navigating again)</div>;

  const exportWorkspace = (
    <div className="video-export-workspace" aria-hidden="true">
      <Workspace
        store={store}
        pageControlsEnabled={false}
        renderOnlyActivePage={false}
      />
    </div>
  );

  if (loading) {
    return (
      <>
        {exportWorkspace}
        <div className="flex flex-col items-center justify-center h-full">
          <div className="w-48 h-48 bg-gray-200 animate-pulse rounded-lg"></div>
          <p className="mt-4 text-gray-500 italic">Rendering Video preview...</p>
        </div>
      </>
    );
  }

  if (videoUrl) {
    console.log("videoUrl:", videoUrl);
  }

  const renderDiagnostics = () => (
    <div
      style={{
        marginTop: 14,
        border: "1px solid rgba(120, 120, 120, 0.22)",
        borderRadius: 14,
        padding: "12px 14px",
        background: "rgba(255, 255, 255, 0.88)",
        color: "#3f3340",
        fontSize: 12,
        lineHeight: 1.5,
        textAlign: "left",
      }}
    >
      <strong style={{ display: "block", marginBottom: 6 }}>Video audio diagnostics</strong>
      <div>Audio tracks in Polotno: {diagnostics.audioCount}</div>
      <div>
        Generated MP4:{" "}
        {diagnostics.videoBlobBytes ? `${Math.round(diagnostics.videoBlobBytes / 1024)} KB` : "not ready"}
        {diagnostics.videoBlobType ? ` (${diagnostics.videoBlobType})` : ""}
      </div>
      {diagnostics.audioReports.length ? (
        <div style={{ marginTop: 8 }}>
          {diagnostics.audioReports.map((report, index) => (
            <div key={`${report.id || report.name}-${index}`} style={{ marginTop: 6 }}>
              <strong>{report.name}</strong>: {report.status}, decode {report.decodeStatus}
              {report.proxied ? ", proxied" : ""}
              {report.contentType ? `, ${report.contentType}` : ""}
              {report.bytes ? `, ${Math.round(report.bytes / 1024)} KB` : ""}
              {report.error ? <div style={{ color: "#9d295d" }}>{report.error}</div> : null}
            </div>
          ))}
        </div>
      ) : null}
      {diagnostics.error ? <div style={{ color: "#9d295d", marginTop: 8 }}>{diagnostics.error}</div> : null}
    </div>
  );

  return (
    <>
      {exportWorkspace}
      <div className="generated-video-shell">
        <video
          src={videoUrl}
          controls
          loop
          className="generated-video-player"
        />
        {renderDiagnostics()}
      </div>
    </>
  );
}
